// Eine Besichtigung ganz vom Gerät nehmen (E3c, S6; PROJEKT.md, O53 und
// O97) – EINE Stelle für drei Anlässe:
//  - Die Kopie „im Büro“, die der Server drei Tage lang nicht mehr nannte
//    (einsicht.ts, kopieLoeschen – der Rückfall, wenn kein Grabstein kommt).
//  - „Das Büro hat gelöscht“: Die Liste „Gesendet“ nennt die Kennung unter
//    `geloescht`, oder ein Eintrag bekam 409 „geloescht“ (einsicht.ts,
//    sender.ts → `vomBueroGeloescht`). Dann kommt die Kopie weg, in JEDEM
//    Stand – auch eine, die noch „in Aufnahme“ ist (Manuels Tipp-Kunde) –,
//    samt allem, was für sie im Postausgang wartet: Nichts davon darf noch
//    hinausgehen, sonst stünde sie am Server wieder auf (Prüfung des Plans
//    E3c, Befund 2). Und einmal ein Hinweis für den Verkäufer (Startseite).
//  - „Besichtigung verwerfen“ vom Verkäufer selbst (arbeitskopie.ts,
//    besichtigungVerwerfen): weg am Gerät, danach „verwerfen“ an den Server.
//
// Was mitgeht: Stationen, Fotos, Notizen, Memos (samt Teilstücken), Längen,
// Material, die Dachplanung, die Nachträge, der Postausgang dieser
// Besichtigung – und die Dateien, die danach kein Foto und kein Memo am
// Gerät mehr braucht. Alles in einer Transaktion: Danach ist alles weg oder
// nichts.

import { speicher, stueckeTabellen, type Aufnahme } from "./speicher.ts";
import { dateiWegWennUnbenutzt } from "./dateien.ts";

/** Die Tabellen, die jede Transaktion mitnimmt, in der eine Besichtigung weggeräumt wird. */
export function wegraeumTabellen() {
  return [speicher.aufnahmen, ...stueckeTabellen(), speicher.memoTeile, speicher.k2, speicher.nachtraege, speicher.dateien, speicher.postausgang, speicher.hinweise] as const;
}

/**
 * Nimmt alles dieser Besichtigung vom Gerät – in einer Transaktion mit
 * `wegraeumTabellen()`. Gibt die Besichtigung zurück, wie sie war (für den
 * Hinweis), oder undefined, wenn es sie nicht (mehr) gab.
 */
export async function besichtigungWegraeumen(aufnahmeId: string): Promise<Aufnahme | undefined> {
  const a = await speicher.aufnahmen.get(aufnahmeId);
  if (!a) return undefined;
  const fotos = await speicher.fotos.where("aufnahmeId").equals(aufnahmeId).toArray();
  const memos = await speicher.memos.where("aufnahmeId").equals(aufnahmeId).toArray();
  for (const m of memos) await speicher.memoTeile.where("memoId").equals(m.id).delete();
  await speicher.memos.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.fotos.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.notizen.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.laengen.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.materialangaben.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.stationen.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.k2.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.nachtraege.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.postausgang.where("aufnahmeId").equals(aufnahmeId).delete();
  await speicher.aufnahmen.delete(aufnahmeId);
  for (const summe of new Set([...fotos.map((f) => f.pruefsumme), ...memos.flatMap((m) => (m.pruefsumme ? [m.pruefsumme] : []))])) {
    await dateiWegWennUnbenutzt(summe);
  }
  return a;
}

/**
 * Das Büro hat diese Besichtigung gelöscht: die Kopie weg, in jedem Stand,
 * samt Postausgang – und einmal ein Hinweis mit Name und Adresse, damit der
 * Verkäufer weiß, welche. Gibt zurück, ob es sie am Gerät gab.
 */
export async function vomBueroGeloescht(aufnahmeId: string, jetzt = new Date()): Promise<boolean> {
  return speicher.transaction("rw", wegraeumTabellen(), async () => {
    const a = await besichtigungWegraeumen(aufnahmeId);
    if (!a) return false;
    await speicher.hinweise.add({ art: "geloescht", name: a.kunde.name, ort: `${a.objekt.strasse}, ${a.objekt.ort}`, wann: jetzt.toISOString() });
    return true;
  });
}
